import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';
import type { ApiRingTemplate } from '../types/api';
import { GLOW_COLORS, type GlowColorOption } from '../types/api';
import type { StyleMode } from '../types/patterns';

/* ── State shape ─────────────────────────────────────────────── */

interface NeoState {
    /** Whether dark mode is active */
    isDark: boolean;

    /**
     * 0–1 normalised page scroll progress.
     * Updated by Lenis scroll listener, read by Three.js CameraRig.
     */
    scrollProgress: number;

    /**
     * Fractional scroll position [0–1] at which the dark-mode transition fires.
     * Tied to the DarkModeTransitionMarker intersection in the page.
     */
    darkModeThreshold: number;

    /** Selected glow colour for the fluorescent material */
    glowColor: GlowColorOption;

    /** Selected gem id for the configurator (null = no gem) */
    selectedGemId: string | null;

    /** Selected alloy id for the configurator */
    selectedAlloyId: string | null;

    /** Ring templates loaded from the API */
    ringTemplates: ApiRingTemplate[];

    /** Currently selected ring template id (null = first available) */
    selectedTemplateId: string | null;

    /**
     * Unified style mode — drives both the 3D pattern shader and the gem selector.
     * 'gems' → gem sub-selector visible; no shader pattern.
     * 'groove' / 'wave' → shader emissive pattern; gem cleared.
     */
    styleMode: StyleMode;

    /** Ring size in mm (circumference, e.g. 54) */
    ringSize: number;

    /** Ring width in mm (e.g. 4) */
    ringWidth: number;

    /** Ring height in mm (e.g. 2) */
    ringHeight: number;

    methods: {
        setIsDark: (v: boolean) => void;
        toggleDark: () => void;
        setScrollProgress: (v: number) => void;
        setDarkModeThreshold: (v: number) => void;
        setGlowColor: (color: GlowColorOption) => void;
        setSelectedGemId: (id: string | null) => void;
        setSelectedAlloyId: (id: string | null) => void;
        setRingTemplates: (templates: ApiRingTemplate[]) => void;
        setSelectedTemplateId: (id: string | null) => void;
        setRingMeasurements: (size: number, width: number, height: number) => void;
        setStyleMode: (mode: StyleMode) => void;
    };
}

/* ── Store ───────────────────────────────────────────────────── */

const useStore = create<NeoState>()(
    subscribeWithSelector(
        immer((set) => ({
            isDark: false,
            scrollProgress: 0,
            darkModeThreshold: 0.55,
            glowColor: GLOW_COLORS[0],
            selectedGemId: null,
            selectedAlloyId: null,
            ringTemplates: [],
            selectedTemplateId: null,
            styleMode: 'gems' as StyleMode,
            ringSize: 54,
            ringWidth: 4,
            ringHeight: 2,

            methods: {
                setIsDark: (v) =>
                    set((s) => {
                        s.isDark = v;
                    }),

                toggleDark: () =>
                    set((s) => {
                        s.isDark = !s.isDark;
                    }),

                setScrollProgress: (v) =>
                    set((s) => {
                        s.scrollProgress = v;
                    }),

                setDarkModeThreshold: (v) =>
                    set((s) => {
                        s.darkModeThreshold = v;
                    }),

                setGlowColor: (color) =>
                    set((s) => {
                        s.glowColor = color;
                    }),

                setSelectedGemId: (id) =>
                    set((s) => {
                        s.selectedGemId = id;
                    }),

                setSelectedAlloyId: (id) =>
                    set((s) => {
                        s.selectedAlloyId = id;
                    }),

                setRingTemplates: (templates) =>
                    set((s) => {
                        s.ringTemplates = templates;
                    }),

                setSelectedTemplateId: (id) =>
                    set((s) => {
                        s.selectedTemplateId = id;
                    }),

                setRingMeasurements: (size, width, height) =>
                    set((s) => {
                        s.ringSize = size;
                        s.ringWidth = width;
                        s.ringHeight = height;
                    }),

                setStyleMode: (mode) =>
                    set((s) => {
                        s.styleMode = mode;
                        // Clear gem when switching to a pattern mode
                        if (mode !== 'gems') {
                            s.selectedGemId = null;
                        }
                        // Auto-select first available gem when switching to gems mode
                        if (mode === 'gems' && s.selectedGemId === null) {
                            const gems = s.ringTemplates[0]?.ringPartials[0]?.gems ?? [];
                            s.selectedGemId = gems[0]?.id ?? null;
                        }
                    }),
            },
        })),
    ),
);

export default useStore;

/* ── Subscriptions (dark mode ↔ CSS class) ───────────────────── */

/**
 * Keeps the `<html class="dark">` in sync with the store.
 * Called once at app startup (see main.tsx).
 */
export function initDarkModeSync(): () => void {
    const apply = (isDark: boolean) =>
        document.documentElement.classList.toggle('dark', isDark);

    // Apply current value immediately
    apply(useStore.getState().isDark);

    // Subscribe to future changes
    return useStore.subscribe((s) => s.isDark, apply);
}

/**
 * Syncs the active glow colour to the CSS `--glow-color` variable
 * so the HTML overlay text can use it too. Call once at startup.
 */
export function initGlowColorSync(): () => void {
    const apply = (c: GlowColorOption) =>
        document.documentElement.style.setProperty('--glow-color', c.cssValue);

    apply(useStore.getState().glowColor);
    return useStore.subscribe((s) => s.glowColor, apply);
}
